import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Analytics as AnalyticsIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Verified as VerifiedIcon,
  Gavel as GavelIcon,
  FactCheck as FactCheckIcon,
  AutoFixHigh as AutoFixHighIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import { useNavigate } from 'react-router-dom';
import {
  getPressReleases,
  getVerificationStatus,
  PressReleaseResponse,
  VerificationStatusApiResponse
} from '../../services/prApi';

interface VerificationItem {
  id: string;
  prTitle: string;
  prId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'requires_review';
  submittedAt: string;
  completedAt?: string;
  submittedBy: string;
  verificationSteps: {
    step: string;
    status: 'pending' | 'completed' | 'failed' | 'in_progress';
    score: number;
    details: string;
    timestamp?: string;
  }[];
  overallScore: number;
  issues: {
    type: 'error' | 'warning' | 'suggestion';
    message: string;
    severity: 'low' | 'medium' | 'high';
    suggestion?: string;
  }[];
  tags: string[];
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

const Verification: React.FC = () => {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState<VerificationItem[]>([]);
  const [filteredVerifications, setFilteredVerifications] = useState<VerificationItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVerification, setSelectedVerification] = useState<VerificationItem | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [newVerificationDialogOpen, setNewVerificationDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availablePRs, setAvailablePRs] = useState<PressReleaseResponse[]>([]);
  const [selectedPrForNew, setSelectedPrForNew] = useState<string>('');
  const [selectedPriorityForNew, setSelectedPriorityForNew] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');

  useEffect(() => {
    const loadVerifications = async () => {
      try {
        setLoading(true);
        setError(null);

        const prs = await getPressReleases();
        setAvailablePRs(prs);

        const items: VerificationItem[] = [];

        await Promise.all(
          prs.map(async (pr) => {
            try {
              const verification: VerificationStatusApiResponse = await getVerificationStatus(pr._id);

              const backendStatus = verification.verificationStatus || pr.verificationStatus || 'Pending';
              const normalized = backendStatus.toString().toLowerCase().replace(/\s+/g, '_');

              let status: VerificationItem['status'] = 'pending';
              if (normalized === 'verified') status = 'completed';
              else if (normalized === 'partially_verified' || backendStatus === 'Partially Verified') status = 'in_progress';
              else if (normalized === 'needs_review' || backendStatus === 'Needs Review') status = 'requires_review';
              else if (normalized === 'rejected') status = 'failed';

              const ai = verification.aiAnalysis || pr.aiAnalysis || {};
              
              // Calculate overall score from confidenceScore or from individual scores
              let overallScore = 0;
              
              // First try to use confidenceScore
              if (typeof ai.confidenceScore === 'number' && ai.confidenceScore > 0) {
                overallScore = ai.confidenceScore;
                // Convert from 0-1 scale to 0-100 if needed
                if (overallScore > 0 && overallScore <= 1) {
                  overallScore = overallScore * 100;
                }
              } else {
                // Calculate from individual verification step scores
                const scores: number[] = [];
                if (typeof ai.structureScore === 'number' && ai.structureScore > 0) {
                  scores.push(ai.structureScore);
                }
                if (typeof ai.readabilityScore === 'number' && ai.readabilityScore > 0) {
                  scores.push(ai.readabilityScore);
                }
                if (typeof ai.seoScore === 'number' && ai.seoScore > 0) {
                  scores.push(ai.seoScore);
                }
                if (typeof ai.contactCompleteness === 'number' && ai.contactCompleteness > 0) {
                  scores.push(ai.contactCompleteness);
                }
                
                // Calculate average if we have scores
                if (scores.length > 0) {
                  overallScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
                }
              }
              
              overallScore = Math.round(overallScore || 0);

              const issues: VerificationItem['issues'] = [];

              if (Array.isArray(ai.suggestions)) {
                ai.suggestions.forEach((s: string) => {
                  issues.push({
                    type: 'suggestion',
                    message: s,
                    severity: overallScore >= 80 ? 'low' : overallScore >= 60 ? 'medium' : 'high',
                    suggestion: s
                  });
                });
              }

              if (status === 'requires_review' || status === 'failed') {
                issues.push({
                  type: status === 'failed' ? 'error' : 'warning',
                  message: `Verification status: ${backendStatus}`,
                  severity: status === 'failed' ? 'high' : 'medium'
                });
              }

              const priority: VerificationItem['priority'] =
                status === 'failed'
                  ? 'urgent'
                  : status === 'requires_review'
                  ? 'high'
                  : status === 'in_progress'
                  ? 'medium'
                  : 'low';

              const verificationItem: VerificationItem = {
                id: pr._id,
                prTitle: pr.title,
                prId: pr._id,
                status,
                submittedAt: pr.createdAt,
                completedAt: status === 'completed' ? pr.updatedAt : undefined,
                submittedBy: pr.author?.fullName || 'Unknown',
                verificationSteps: [
                  {
                    step: 'Content Analysis',
                    status: (typeof ai.structureScore === 'number' && ai.structureScore > 0) ? 'completed' : 'pending',
                    score: typeof ai.structureScore === 'number' ? ai.structureScore : 0,
                    details: 'AI content structure analysis',
                    timestamp: (typeof ai.structureScore === 'number' && ai.structureScore > 0) ? pr.updatedAt : undefined
                  },
                  {
                    step: 'Readability',
                    status: (typeof ai.readabilityScore === 'number' && ai.readabilityScore > 0) ? 'completed' : 'pending',
                    score: typeof ai.readabilityScore === 'number' ? ai.readabilityScore : 0,
                    details: 'Readability and clarity analysis',
                    timestamp: (typeof ai.readabilityScore === 'number' && ai.readabilityScore > 0) ? pr.updatedAt : undefined
                  },
                  {
                    step: 'SEO Optimization',
                    status: (typeof ai.seoScore === 'number' && ai.seoScore > 0) ? 'completed' : 'pending',
                    score: typeof ai.seoScore === 'number' ? ai.seoScore : 0,
                    details: 'SEO keyword and meta analysis',
                    timestamp: (typeof ai.seoScore === 'number' && ai.seoScore > 0) ? pr.updatedAt : undefined
                  },
                  {
                    step: 'Contact Completeness',
                    status: (typeof ai.contactCompleteness === 'number' && ai.contactCompleteness > 0) ? 'completed' : 'pending',
                    score: typeof ai.contactCompleteness === 'number' ? ai.contactCompleteness : 0,
                    details: 'Contact and company information completeness',
                    timestamp: (typeof ai.contactCompleteness === 'number' && ai.contactCompleteness > 0) ? pr.updatedAt : undefined
                  }
                ],
                overallScore,
                issues,
                tags: pr.targetAudience?.slice(0, 3) || [],
                priority
              };

              items.push(verificationItem);
            } catch (err) {
              console.error('Failed to load verification for PR', pr._id, err);
            }
          })
        );

        setVerifications(items);
        setFilteredVerifications(items);
      } catch (err: any) {
        console.error('Error loading verifications:', err);
        setError(err.message || 'Failed to load verifications');
      } finally {
        setLoading(false);
      }
    };

    loadVerifications();
  }, []);

  // Filter verifications
  useEffect(() => {
    let filtered = verifications;

    if (searchTerm) {
      filtered = filtered.filter(verification => 
        verification.prTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        verification.submittedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        verification.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(verification => verification.status === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(verification => verification.priority === priorityFilter);
    }

    setFilteredVerifications(filtered);
  }, [searchTerm, statusFilter, priorityFilter, verifications]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon style={{ color: '#4CAF50' }} />;
      case 'in_progress': return <PendingIcon style={{ color: '#FF9800' }} />;
      case 'failed': return <ErrorIcon style={{ color: '#F44336' }} />;
      case 'requires_review': return <WarningIcon style={{ color: '#FF9800' }} />;
      case 'pending': return <ScheduleIcon style={{ color: '#2196F3' }} />;
      default: return <PendingIcon style={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'in_progress': return '#FF9800';
      case 'failed': return '#F44336';
      case 'requires_review': return '#FF9800';
      case 'pending': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#F44336';
      case 'high': return '#FF9800';
      case 'medium': return '#2196F3';
      case 'low': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'error': return <ErrorIcon style={{ color: '#F44336' }} />;
      case 'warning': return <WarningIcon style={{ color: '#FF9800' }} />;
      case 'suggestion': return <AutoFixHighIcon style={{ color: '#2196F3' }} />;
      default: return <WarningIcon style={{ color: '#9E9E9E' }} />;
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, verification: VerificationItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedVerification(verification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVerification(null);
  };

  const handleViewVerification = () => {
    setViewDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedVerification for dialog
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNavigateAnalytics = () => {
    navigate('/analytics');
  };

  const handleStartNewVerification = () => {
    setNewVerificationDialogOpen(false);
    navigate('/create-pr');
  };

  const handleContinueVerification = (verification: VerificationItem) => {
    // Store PR ID and navigate to verification step
    localStorage.setItem('prId', verification.prId);
    localStorage.setItem('prCurrentStep', '3'); // Step 3 is Verification
    localStorage.setItem('isEditingPR', 'true');
    // Navigate directly to verification step
    navigate('/create-pr/verification');
  };

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SecurityIcon style={{ fontSize: '32px' }} />
          Verification
        </Typography>
        <Typography variant="body1" style={{ color: '#666' }}>
          Verify and validate your press releases before publication
        </Typography>
      </div>

      {/* Stats Cards */}
      <Grid container spacing={3} style={{ marginBottom: '32px' }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                    {verifications.length}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Total Verifications
                  </Typography>
                </div>
                <SecurityIcon style={{ fontSize: '40px', color: '#0927eb', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    {verifications.filter(v => v.status === 'completed').length}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Completed
                  </Typography>
                </div>
                <CheckCircleIcon style={{ fontSize: '40px', color: '#4CAF50', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                    {verifications.filter(v => v.status === 'in_progress').length}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    In Progress
                  </Typography>
                </div>
                <PendingIcon style={{ fontSize: '40px', color: '#FF9800', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                    {(() => {
                      // Calculate average from all verifications with scores > 0
                      const verificationsWithScores = verifications.filter(v => v.overallScore > 0);
                      return verificationsWithScores.length > 0
                        ? Math.round(verificationsWithScores.reduce((sum, v) => sum + v.overallScore, 0) / verificationsWithScores.length)
                        : 0;
                    })()}%
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Avg Score
                  </Typography>
                </div>
                <AssessmentIcon style={{ fontSize: '40px', color: '#2196F3', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Filters and Search */}
      <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search verifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="requires_review">Requires Review</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                label="Priority"
              >
                <MenuItem value="all">All Priority</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<AnalyticsIcon />}
              fullWidth
              style={{
                borderColor: '#000000',
                color: '#000000'
              }}
              onClick={handleNavigateAnalytics}
            >
              Analytics
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => setNewVerificationDialogOpen(true)}
              style={{
                backgroundColor: '#000000',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              New Verification
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Verifications Table */}
      <TableContainer component={Paper} style={{ borderRadius: '12px' }}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 'bold' }}>PR Title</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Priority</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Overall Score</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Submitted By</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Submitted</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVerifications
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((verification) => (
                <TableRow 
                  key={verification.id} 
                  hover
                  onClick={(e) => {
                    // Only redirect if clicking on the row (not on action buttons)
                    if ((e.target as HTMLElement).closest('button, a, [role="button"]')) {
                      return;
                    }
                    // Redirect to verification step if status is pending or in_progress
                    if (verification.status === 'pending' || verification.status === 'in_progress' || verification.status === 'requires_review') {
                      handleContinueVerification(verification);
                    }
                  }}
                  style={{ 
                    cursor: (verification.status === 'pending' || verification.status === 'in_progress' || verification.status === 'requires_review') 
                      ? 'pointer' 
                      : 'default' 
                  }}
                >
                  <TableCell>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {verification.prTitle}
                      </Typography>
                      <div style={{ marginTop: '4px' }}>
                        {verification.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            style={{ marginRight: '4px', marginBottom: '2px' }}
                          />
                        ))}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(verification.status)}
                      label={verification.status.replace('_', ' ').charAt(0).toUpperCase() + verification.status.replace('_', ' ').slice(1)}
                      style={{
                        backgroundColor: getStatusColor(verification.status),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={verification.priority.charAt(0).toUpperCase() + verification.priority.slice(1)}
                      style={{
                        backgroundColor: getPriorityColor(verification.priority),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: verification.overallScore > 80 ? '#4CAF50' : verification.overallScore > 60 ? '#FF9800' : '#F44336' }}>
                        {verification.overallScore}%
                      </Typography>
                      <div style={{ width: '60px' }}>
                        <LinearProgress
                          variant="determinate"
                          value={verification.overallScore}
                          style={{
                            height: '6px',
                            borderRadius: '3px',
                            backgroundColor: 'rgba(9, 39, 235, 0.2)'
                          }}
                        />
                      </div>
                      {(verification.status === 'pending' || verification.status === 'in_progress' || verification.status === 'requires_review') && (
                        <Tooltip title="Click row to continue verification">
                          <Chip 
                            label="Continue" 
                            size="small" 
                            style={{ 
                              backgroundColor: '#0927eb', 
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                              marginLeft: '8px'
                            }} 
                          />
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Avatar style={{ width: '24px', height: '24px', fontSize: '12px' }}>
                        {verification.submittedBy.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      {verification.submittedBy}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CalendarIcon style={{ fontSize: '16px', color: '#666' }} />
                      {new Date(verification.submittedAt).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuClick(e, verification)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredVerifications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewVerification}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedVerification) {
            handleMenuClose();
            handleContinueVerification(selectedVerification);
          }
        }}>
          <ListItemIcon>
            <PlayIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Continue Verification</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedVerification) {
              // Store PR ID and step in localStorage for the PR submission interface
              localStorage.setItem('prId', selectedVerification.prId);
              localStorage.setItem('prCurrentStep', '3'); // Step 3 is Verification
              localStorage.setItem('isEditingPR', 'true');
              localStorage.setItem('prVerificationComplete', 'false'); // Reset verification status
              handleMenuClose();
              navigate('/create-pr/verification');
            }
          }}
        >
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Re-run Verification</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {}} style={{ color: '#F44336' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" style={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Verification Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedVerification(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
            {selectedVerification?.prTitle}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedVerification && (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Verification Overview
                  </Typography>
                  <div style={{ marginBottom: '16px' }}>
                    <Chip
                      icon={getStatusIcon(selectedVerification.status)}
                      label={selectedVerification.status.replace('_', ' ').charAt(0).toUpperCase() + selectedVerification.status.replace('_', ' ').slice(1)}
                      style={{
                        backgroundColor: getStatusColor(selectedVerification.status),
                        color: 'white',
                        fontWeight: 'bold',
                        marginRight: '8px'
                      }}
                    />
                    <Chip
                      label={selectedVerification.priority.charAt(0).toUpperCase() + selectedVerification.priority.slice(1)}
                      style={{
                        backgroundColor: getPriorityColor(selectedVerification.priority),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Overall Score
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography variant="h6" style={{ fontWeight: 'bold', color: selectedVerification.overallScore > 80 ? '#4CAF50' : selectedVerification.overallScore > 60 ? '#FF9800' : '#F44336' }}>
                        {selectedVerification.overallScore}%
                      </Typography>
                      <div style={{ width: '200px' }}>
                        <LinearProgress
                          variant="determinate"
                          value={selectedVerification.overallScore}
                          style={{
                            height: '8px',
                            borderRadius: '4px',
                            backgroundColor: 'rgba(9, 39, 235, 0.2)'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Submitted By
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedVerification.submittedBy}
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Submitted At
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {new Date(selectedVerification.submittedAt).toLocaleString()}
                    </Typography>
                  </div>
                  {selectedVerification.completedAt && (
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Completed At
                      </Typography>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {new Date(selectedVerification.completedAt).toLocaleString()}
                      </Typography>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Issues Found
                  </Typography>
                  {selectedVerification.issues.length === 0 ? (
                    <Alert severity="success">
                      No issues found! Verification completed successfully.
                    </Alert>
                  ) : (
                    <div>
                      {selectedVerification.issues.map((issue, index) => (
                        <Alert
                          key={index}
                          severity={issue.type === 'error' ? 'error' : issue.type === 'warning' ? 'warning' : 'info'}
                          style={{ marginBottom: '8px' }}
                        >
                          <div>
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                              {issue.message}
                            </Typography>
                            {issue.suggestion && (
                              <Typography variant="body2" style={{ marginTop: '4px' }}>
                                <strong>Suggestion:</strong> {issue.suggestion}
                              </Typography>
                            )}
                          </div>
                        </Alert>
                      ))}
                    </div>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Verification Steps
                  </Typography>
                  <Stepper orientation="vertical">
                    {selectedVerification.verificationSteps.map((step, index) => (
                      <Step key={index} active={step.status === 'completed' || step.status === 'in_progress'}>
                        <StepLabel
                          icon={step.status === 'completed' ? <CheckCircleIcon /> : step.status === 'in_progress' ? <PendingIcon /> : <ScheduleIcon />}
                        >
                          {step.step}
                        </StepLabel>
                        <StepContent>
                          <div style={{ marginBottom: '16px' }}>
                            <Typography variant="body2" style={{ color: '#666', marginBottom: '8px' }}>
                              {step.details}
                            </Typography>
                            {step.score > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: step.score > 80 ? '#4CAF50' : step.score > 60 ? '#FF9800' : '#F44336' }}>
                                  Score: {step.score}%
                                </Typography>
                                <div style={{ width: '100px' }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={step.score}
                                    style={{
                                      height: '6px',
                                      borderRadius: '3px',
                                      backgroundColor: 'rgba(9, 39, 235, 0.2)'
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            {step.timestamp && (
                              <Typography variant="caption" style={{ color: '#666' }}>
                                Completed: {new Date(step.timestamp).toLocaleString()}
                              </Typography>
                            )}
                          </div>
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>
              </Grid>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewDialogOpen(false);
            setSelectedVerification(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Verification Dialog */}
      <Dialog
        open={newVerificationDialogOpen}
        onClose={() => setNewVerificationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
            Start New Verification
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} style={{ marginTop: '16px' }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select PR to Verify</InputLabel>
                <Select
                  label="Select PR to Verify"
                  value={selectedPrForNew}
                  onChange={(e) => setSelectedPrForNew(e.target.value as string)}
                >
                  {availablePRs.map((pr) => (
                    <MenuItem key={pr._id} value={pr._id}>
                      {pr.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Priority Level</InputLabel>
                <Select
                  label="Priority Level"
                  value={selectedPriorityForNew}
                  onChange={(e) => setSelectedPriorityForNew(e.target.value as any)}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                placeholder="Add any specific verification requirements or notes"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVerificationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
            onClick={handleStartNewVerification}
          >
            Start Verification
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Verification;